import { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from '@turf/turf';
import { createImageSchema, imageSchema } from 'utils/sharedSchema.utils';
import * as Yup from 'yup';

export interface IPlotsValues {
  featureCollection: FeatureCollection<Polygon | MultiPolygon | Point, { plotId?: string }>;
}

export interface IPlotValues {
  title: string;
  size: number | null;
  plotId: string;
  farmId: string | null;
  feature: Feature<Polygon | Point | MultiPolygon, { plotId?: string }>;
  isAgroforestry?: boolean;
}

export const generatePlotSchema = (isCreate: boolean) => {
  return Yup.object().shape({
    title: Yup.string().default(''),
    plotId: !isCreate
      ? Yup.string().default('')
      : Yup.string().default('').required('Plot ID is required'),
    farmId: Yup.string().default('').nullable(),
    size: Yup.number()
      .positive('Plot size must be a positive value')
      .when('feature', {
        is: (feature: Feature<Polygon | Point | MultiPolygon> | null) => {
          return feature?.geometry.type === 'Point';
        },
        then: Yup.number().required('Plot size is required when a point feature is used'),
        otherwise: Yup.number().nullable(),
      }),
    feature: Yup.object()
      .required('Please draw a plot on the map or input your raw plot data')
      .nullable(),
    isAgroforestry: Yup.boolean().nullable(),
  }) as Yup.ObjectSchema<IPlotValues>;
};

export const plotsSchema = Yup.object().shape({
  featureCollection: Yup.object(),
}) as Yup.ObjectSchema<IPlotsValues>;

export const farmSchema = Yup.object().shape({
  rawMaterialId: Yup.string().default('').required('This field is required'),
  sizeHa: Yup.number().positive('Must be a positive value').nullable(),
  numberOfFarmers: Yup.number()
    .positive('Must be a positive value')
    .integer('Must be an integer without decimal values')
    .nullable(),
  additionalRawMaterialIds: Yup.array().of(Yup.string()).default([]),
  landOwnership: Yup.string().nullable(),
}) as Yup.ObjectSchema;

export const siteCreationSchema = Yup.object().shape({
  image: createImageSchema({
    message: 'Please add an image of your site',
    required: false,
  }),
  title: Yup.string().default('').required('This field is required'),
  ownedBy: Yup.object()
    .shape({ id: Yup.string(), title: Yup.string(), logo: imageSchema })
    .nullable()
    .default(null),
  siteType: Yup.string().default('').required('This field is required'),
  locationName: Yup.string(),
  externalId: Yup.string().default('').nullable(),
  description: Yup.string().default('').nullable(),
  farmData: Yup.mixed()
    .when('siteType', {
      is: 'FARM',
      then: farmSchema,
      otherwise: Yup.object().shape({}).notRequired(),
    })
    .default(farmSchema.default()),
  useExactCoordinates: Yup.boolean().default(false),
  locationCoordinates: Yup.object().when('useExactCoordinates', {
    is: true,
    then: Yup.object()
      .shape({
        lat: Yup.number(),
        lng: Yup.number(),
      })
      .required('Location is a required field'),
    otherwise: Yup.object()
      .shape({
        lat: Yup.number(),
        lng: Yup.number(),
      })
      .nullable(),
  }),
  mapboxId: Yup.string().when('useExactCoordinates', {
    is: false,
    then: Yup.string().required('Location is a required field'),
    otherwise: Yup.string().nullable(),
  }),
});

export const siteEditingSchema = Yup.object().shape({
  image: createImageSchema({
    message: 'Please add an image of your site',
    required: false,
  }),
  title: Yup.string().default('').required('This field is required'),
  ownedBy: Yup.object()
    .shape({ id: Yup.string(), title: Yup.string(), logo: imageSchema })
    .nullable()
    .default(null),
  siteType: Yup.string().default('').required('This field is required'),
  locationName: Yup.string(),
  mapboxId: Yup.string().nullable(),
  externalId: Yup.string().default('').nullable(),
  description: Yup.string().default('').nullable(),
  farmData: Yup.mixed()
    .when('siteType', {
      is: 'FARM',
      then: farmSchema,
      otherwise: Yup.object().shape({}).notRequired(),
    })
    .default(farmSchema.default()),
  useExactCoordinates: Yup.boolean().default(false),
  locationCoordinates: Yup.object().when('useExactCoordinates', {
    is: true,
    then: Yup.object()
      .shape({
        lat: Yup.number(),
        lng: Yup.number(),
      })
      .required('Location is a required field'),
    otherwise: Yup.object()
      .shape({
        lat: Yup.number(),
        lng: Yup.number(),
      })
      .nullable(),
  }),
}) as Yup.ObjectSchema;

export const siteClusterSchema = Yup.object().shape({
  image: createImageSchema({
    message: 'Please add an image of your site',
    required: false,
  }),
  mapboxId: Yup.string().nullable(),
  externalId: Yup.string().default('').nullable(),
  title: Yup.string().default('').required('This field is required'),
  ownedBy: Yup.object()
    .shape({ id: Yup.string(), title: Yup.string(), logo: imageSchema })
    .nullable()
    .default(null),
  locationCoordinates: Yup.object()
    .shape({
      lat: Yup.number(),
      lng: Yup.number(),
    })
    .required('Location is a required field'),
  locationName: Yup.string().label('Location name').default('').required('This field is required'),
}) as Yup.ObjectSchema;
